import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { brandingOptions, corporateFacts, corporateExamples } from '../data/siteContent';
import { PageHero } from '../components/PageHero';
import { ImageCarousel } from '../components/ImageCarousel';
import { SectionHeading } from '../components/SectionHeading';
import { EnquiryForm } from '../components/EnquiryForm';
const EASE = [0.23, 1, 0.32, 1] as const;
const valueProps = [{
  title: 'It gets used, not stored',
  body: 'A branded cup goes to the office, the gym and the school run. Your logo travels with it instead of sitting in a drawer.'
}, {
  title: 'Made to be kept',
  body: 'Bamboo fibre, dishwasher safe, replaceable lids and sleeves. Not a giveaway that lands in the bin by Friday.'
}, {
  title: 'One supplier, start to finish',
  body: 'Artwork, proof, production and nationwide delivery handled here. You approve a proof and we do the rest.'
}];

/**
 * /corporate-branding — standalone landing page for paid campaigns.
 * Single conversion goal: send the brief.
 */
export function CorporateBranding() {
  return <div className="w-full bg-paper" data-id="element-366" data-mp="xaqb9kmrmvlw">
      <PageHero tone="coffee" eyebrow="Corporate gifting & custom branding" title="Your brand, on a cup people keep." intro="Branded reusable cups for staff gifts, conferences, cafés and client thank-yous. From 50 units, delivered nationwide in three to six weeks." data-id="element-367" data-mp="x9ot6w7c18uo">
        <div className="flex flex-wrap items-center gap-6" data-id="element-368" data-mp="1bybcf902ykr6">
          <a href="#brief" className="type-cta rounded-full bg-paper px-9 py-4 text-[11px] text-coffee transition-colors hover:bg-oat" data-id="element-369" data-mp="1g1hmkrqbcw2x">
            Request a quote
          </a>
          <a href="#options" className="type-cta text-[11px] text-paper/70 underline decoration-paper/30 underline-offset-[6px] transition-colors hover:text-paper" data-id="element-370" data-mp="3hue9bbtl5n2v">
            See the three options
          </a>
        </div>
      </PageHero>

      {/* Why a branded cup */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-371" data-mp="p07075sf82z7">
        <div className="grid gap-x-12 gap-y-10 lg:grid-cols-3" data-id="element-372" data-mp="12yng9nc6b5u0">
          {valueProps.map((v, i) => <motion.div key={v.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45,
          delay: i * 0.05,
          ease: EASE
        }} className="border-t border-oat pt-7" data-id="element-373" data-mp="xy97u46nttvg" data-mpi={i}>
              <h2 className="type-display text-2xl text-coffee md:text-3xl" data-id="element-374" data-mp="1lqzlqiy4in8d">
                {v.title}
              </h2>
              <p className="type-body mt-3 text-sm text-ink/65" data-id="element-375" data-mp="1vmt4ik2frcyl">{v.body}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Three branding routes */}
      <section id="options" className="w-full scroll-mt-28 bg-paper-warm py-20 md:py-28" data-id="element-376" data-mp="1qlvejlb6pemj">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-377" data-mp="2f8ulss8pc70p">
          <SectionHeading eyebrow="Three ways to brand" title="Pick the route that fits the budget." subtitle="Everything you need to brief a run is on this page — no PDF to download, no sales call to book first." data-id="element-378" data-mp="2f8ci5ajpmqir" />

          <div className="mt-14 grid gap-6 lg:grid-cols-3" data-id="element-379" data-mp="1riqiklzfe9a8">
            {brandingOptions.map((o, i) => <motion.article key={o.id} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06,
            ease: EASE
          }} className="flex h-full flex-col overflow-hidden rounded-4xl bg-paper text-coffee" data-id="element-380" data-mp="1wa8l3eh3khxb" data-mpi={i}>
                <ImageCarousel images={o.images} alt={o.title} data-id="element-381" data-mp="1cky662p6e109" />
                <div className="flex flex-1 flex-col p-8" data-id="element-382" data-mp="15it9cccnrpnv">
                  <h3 className="type-display text-3xl text-coffee" data-id="element-383" data-mp="15i9uj4ib1he2">
                    {o.title}
                  </h3>
                  <p className="type-body mt-4 text-sm text-ink/65" data-id="element-384" data-mp="10m2kazivljqb">
                    {o.body}
                  </p>

                  <dl className="mt-8 space-y-2.5" data-id="element-385" data-mp="qu6knk155ljz">
                    {o.facts.map(f => <div key={f.label} className="flex items-baseline justify-between gap-4 border-b border-oat/70 pb-2.5 last:border-0 last:pb-0" data-id="element-386" data-mp="ms1u4u5zp51k">
                        <dt className="type-body text-sm text-ink/55" data-id="element-387" data-mp="ctcbgz69rgk9">
                          {f.label}
                        </dt>
                        <dd className="type-ui text-sm text-coffee" data-id="element-388" data-mp="7wvmy3v5r06n">
                          {f.value}
                        </dd>
                      </div>)}
                  </dl>

                  <a href="#brief" className="type-cta mt-auto inline-flex items-center gap-2 pt-8 text-[10px] text-pennant" data-id="element-389" data-mp="229v626yqycio">
                    Enquire about this
                    <ArrowRightIcon className="h-3 w-3" strokeWidth={1.5} data-id="element-390" data-mp="2jiea9nthe7zj" />
                  </a>
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      {/* Key facts */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-391" data-mp="1rs046drplzyf">
        <SectionHeading eyebrow="Good to know" title="The detail, up front." data-id="element-392" data-mp="1vstsh892dgru" />
        <div className="mt-14 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-393" data-mp="20s6a9el2vxnk">
          {corporateFacts.map((f, i) => <motion.div key={f.label} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45,
          delay: i * 0.04,
          ease: EASE
        }} className="border-t border-oat pt-6" data-id="element-394" data-mp="25s115i4usn8m" data-mpi={i}>
              <p className="type-eyebrow mb-3 text-[9px] text-coffee/45" data-id="element-395" data-mp="2rju2vbs1qz75">
                {f.label}
              </p>
              <p className="type-display text-2xl text-coffee" data-id="element-396" data-mp="31fx32c3c8nrg">{f.value}</p>
              <p className="type-body mt-2 text-sm text-ink/60" data-id="element-397" data-mp="31e447iyyv7u0">{f.detail}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Enquiry */}
      <section id="brief" className="w-full scroll-mt-28 bg-coffee py-20 text-paper md:py-28" data-id="element-398" data-mp="28sb0961xpn5n">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-399" data-mp="23u9iqpk5pvwa">
          <div className="grid gap-14 lg:grid-cols-[1fr_1.15fr] lg:gap-20" data-id="element-400" data-mp="2ihugs526d4xc">
            <div data-id="element-401" data-mp="2dqmbnl7vo5pz">
              <p className="type-eyebrow text-[11px] text-paper/55" data-id="element-402" data-mp="2dq2qzwquf389">
                Request a quote
              </p>
              <h2 className="type-display mt-5 text-4xl text-paper md:text-5xl" data-id="element-403" data-mp="1q003zv1scngi">
                Send us the brief.
              </h2>
              <p className="type-body mt-5 max-w-sm text-base text-paper/65" data-id="element-404" data-mp="1kzujcytxo17x">
                Tell us roughly how many cups, when you need them, and what you
                want on them. We come back with pricing and a digital proof.
              </p>

              <p className="type-eyebrow mt-14 text-[9px] text-paper/45" data-id="element-405" data-mp="3mrw2qlyeodjf">
                Recent runs
              </p>
              <div className="mt-6 space-y-6" data-id="element-406" data-mp="3rqz3hu7crxll">
                {corporateExamples.map(e => <div key={e.name} className="flex items-center gap-4" data-id="element-407" data-mp="jbp5ax6l3cj3">
                    <img src={e.image} alt="" className="h-16 w-16 shrink-0 rounded-2xl object-cover" data-id="element-408" data-mp="o9qqbnnkllx7" />
                    <div data-id="element-409" data-mp="earq8tgveylt">
                      <p className="type-ui text-sm text-paper" data-id="element-410" data-mp="e9zu9hjd2yv0">{e.name}</p>
                      <p className="type-body text-xs text-paper/55" data-id="element-411" data-mp="2uzf16ccppk67">
                        {e.detail}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            <EnquiryForm tone="dark" submitLabel="Send enquiry" note="We reply within one working day." fields={[{
            name: 'company',
            label: 'Company',
            half: true,
            required: true
          }, {
            name: 'name',
            label: 'Your name',
            half: true,
            required: true
          }, {
            name: 'email',
            label: 'Email',
            type: 'email',
            half: true,
            required: true
          }, {
            name: 'phone',
            label: 'Phone',
            type: 'tel',
            half: true
          }, {
            name: 'quantity',
            label: 'Approximate quantity',
            type: 'select',
            half: true,
            options: ['50 – 100', '100 – 250', '250 – 500', '500+'],
            required: true
          }, {
            name: 'branding',
            label: 'Branding method',
            type: 'select',
            half: true,
            options: ['Custom sleeves', 'Printing on solid colour cups', 'Bespoke customisation', 'Not sure yet']
          }, {
            name: 'brief',
            label: 'Tell us about the project',
            type: 'textarea',
            required: true
          }]} data-id="element-412" data-mp="3qqo6ybkaxkl7" />
          </div>
        </div>
      </section>

      {/* Cross-link back to personal gifting */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-413" data-mp="1s9ni4exx8mjj">
        <div className="flex flex-col items-start gap-6 border-t border-oat pt-12 md:flex-row md:items-center md:justify-between" data-id="element-414" data-mp="1x5uqsfhpcc56">
          <div data-id="element-415" data-mp="2672a9rfk78f7">
            <h2 className="type-display text-3xl text-coffee md:text-4xl" data-id="element-416" data-mp="266ss2q0ra5k2">
              Buying for one person instead?
            </h2>
            <p className="type-body mt-3 max-w-md text-sm text-ink/60" data-id="element-417" data-mp="1rjuwi4c2yakj">
              Our gifting page has themed ideas and occasion cards, with no
              minimum order.
            </p>
          </div>
          <Link to="/gifting" className="type-cta shrink-0 rounded-full border border-coffee/25 px-8 py-4 text-[11px] text-coffee transition-colors hover:bg-coffee hover:text-paper" data-id="element-418" data-mp="21f5375dfb64n">
            Personal gifting
          </Link>
        </div>
      </section>
    </div>;
}